/**
 * roomSizeHeat — the room-level market claim, pitched at whatever the corpus
 * can actually back for a room THIS SIZE.
 *
 * 🔴 WHY THIS EXISTS. The league page's headline said "your room paid about
 * 12.4% under the real market" and had no idea how big the room was. Room size
 * moves that number a long way and it is not a fact about anybody's drafting:
 * a room's total money is teams × budget while the benchmark is ONE median per
 * player pooled across every room size, so a 10-team room is bidding less money
 * at the same board and reads "under" however well it drafted. Measured on the
 * Sep 3 2026 corpus (meta.sizeHeat, built by scripts/build_rate_benchmarks.py):
 *
 *     10 teams   −12.4% (sflex, 8 rooms) / −12.2% (1QB, 8 rooms)
 *     12 teams    +3.7%       (62 rooms) /  +3.7%      (42 rooms)
 *     14 teams   +20.0%        (3 rooms) / +20.9%       (6 rooms)
 *
 * A 32-point spread. So a 10-team room told "you paid 12% under the market" was
 * being congratulated for being ten-handed.
 *
 * 🔴 THREE OUTCOMES, NOT TWO (changed Sep 3 2026, chunk Q). The first version
 * of this file had a binary gate — quote the size cell, or make no room-level
 * claim at all — and it threw away a good measurement. The 10-team cells are
 * the TIGHTEST non-trivial cells in the whole artifact: 8 rooms each, ranges of
 * 6.7pp (sflex) and 2.4pp (1QB), and the two formats land 0.17 of a point
 * apart on completely disjoint rooms. Independent cross-format replication at
 * n=8 is evidence. Suppressing it was the wrong kind of caution, so:
 *
 *   rooms >= meta.minRooms       CONFIDENT. The headline carries a percentage.
 *   rooms >= SIZE_HEAT_MIN_ROOMS THIN BUT REAL. The headline carries a
 *     and the spread is sane     DIRECTION and no digits; the note carries the
 *                                measurement, its n, and "read as direction".
 *   anything else                NO room-level claim, and the note says why.
 *
 * That middle tier is the project's own long-standing pattern, in the words the
 * report already uses elsewhere ("from N real rooms so far: read as direction",
 * ReportView.jsx and TeamReportView.jsx). The rule it encodes: a thin number
 * publishes WITH its n on its face; a thin number without its n does not
 * publish at all.
 *
 * 🔴 THE 14-TEAM CELLS STAY SUPPRESSED, AND ONE OF THEM IS WHY THE GUARD
 * EXISTS. Superflex has 3 rooms — under the floor, nothing to discuss. The 1QB
 * cell has 6, and it is five rooms between +15.6% and +21.7% plus one room at
 * +1523.7%. No caveat sentence fixes that: a median over a cluster and a broken
 * record is not a measurement with wide error bars, it is a measurement with a
 * corrupt member. Hence the spread guard below, which would suppress that cell
 * on its own even if six more 14-team rooms arrived tomorrow and cleared the
 * count floor.
 *
 * 🔴 THE CONFIDENT TIER IS STILL meta.minRooms, DELIBERATELY. That is the gate
 * that was here before this change, so the 12-team sentence is unchanged BY
 * CONSTRUCTION rather than by a test that happens to pass. It is borrowed —
 * min_rooms(N) was written as a floor for how many rooms a PLAYER must sell in,
 * not for how many rooms make a median — and the borrowing is worth naming
 * because it also tracks the corpus (it moves on every harvest, so a thickening
 * cell promotes itself from direction to percentage with no edit here). If it
 * ever needs to stop tracking, the replacement is a third constant beside the
 * two below, not a literal at the call site.
 */

import benchmarks from "../data/rateBenchmarks.json";

/**
 * Over, under, or level, on one shared threshold.
 *
 * Shared deliberately: every number in the sentences below is the same
 * estimator on the same scale, so they must round to a word the same way or a
 * sentence can say a room paid "under" a reference it is level with.
 */
export const LEVEL_BAND = 0.5;

/**
 * The floor for saying anything at all about a room size, in ROOMS.
 *
 * 🔴 DISTINCT FROM THE PLAYER TABLE'S meta.minRooms, and it has to be: that one
 * answers "in how many rooms must a PLAYER have sold before his median means
 * something", which is a question about one player's sample inside a cell. This
 * one answers "how many ROOMS make a room-level median worth printing", and the
 * two have no reason to be the same number. Today they are 8 against 14/11, so
 * the difference is exactly the 10-team cells.
 *
 * Eight, because that is where the evidence sits on this corpus and not because
 * it is a round number: the two 10-team cells hold 8 rooms each, span 6.7pp and
 * 2.4pp, and agree with each other to 0.17 of a point across two disjoint sets
 * of leagues. Cross-format replication is the part that makes n=8 publishable —
 * one tight cell could be luck, two tight cells that land on the same answer
 * from different rooms is a measurement. Below 8 there is no cell on this
 * corpus at all, so nothing is being excluded by a hair; the number will want
 * revisiting the first time a 5- or 6-room cell appears and looks tempting.
 */
export const SIZE_HEAT_MIN_ROOMS = 8;

/**
 * The sanity guard, in PERCENTAGE POINTS of range (max − min) across the cell's
 * own rooms.
 *
 * 🔴 A COUNT FLOOR ALONE CANNOT STOP A THIN-AND-WILD CELL. Eight rooms that
 * disagree wildly clear a count floor and produce a median that means nothing,
 * and the corpus already contains the shape of that failure — see the 1QB
 * 14-team cell in this file's header.
 *
 * RANGE, NOT A RATIO TO THE MEDIAN, and that is a considered swap. A ratio is
 * unstable exactly where these cells live: the 12-team medians are +3.65% and
 * +3.70%, so a ratio's denominator is already within a rounding error of zero
 * for the one cell we most want to keep, and the 1QB 12-team cell would read
 * 4.9× on the strength of a single legitimate +18% room. Range has no such
 * failure mode and is deliberately NON-robust, which is the point: this guard
 * is hunting for one corrupt member, so the statistic should be the one a
 * single outlier moves.
 *
 * FIFTY POINTS, sized off the measured cells so it can never fire on real data:
 * the widest legitimate cell in the artifact is the 1QB 12-team one at 19.8pp,
 * and the cell this guard exists to kill runs 1508pp. The threshold sits 2.5×
 * above the former and 30× below the latter. A guard with a dead zone that wide
 * is a guard that will only ever fire on something genuinely broken, which is
 * the only kind worth having in front of published copy.
 */
export const SIZE_HEAT_MAX_SPREAD = 50;

export function heatWord(pct) {
  if (pct > LEVEL_BAND) return "over";
  if (pct < -LEVEL_BAND) return "under";
  return "right at";
}

/** Percent at one decimal — the scale every heat on this page arrives on. */
const pct1 = (frac) => Math.round(frac * 1000) / 10;

/** The raw size cell as the artifact holds it, ungated. Internal. */
function rawCell(benchmarkFormat, teams) {
  const cell = benchmarks[benchmarkFormat];
  const meta = cell && cell.meta;
  const table = meta && meta.sizeHeat;
  if (!table) return null;
  const entry = table[String(teams)];
  if (!entry || !Number.isFinite(entry.heat)) return null;
  const floor = Number(meta.minRooms);
  if (!Number.isFinite(floor)) return null;
  // An artifact built before min/max existed has no spread to judge, and an
  // unjudgeable spread is an infinite one: it fails the guard rather than
  // passing it by default. Missing is never "fine".
  const lo = Number(entry.min);
  const hi = Number(entry.max);
  const known = Number.isFinite(lo) && Number.isFinite(hi);
  // 🔴 THE SPREAD IS THE DIFFERENCE OF THE TWO ROUNDED EXTREMES, not the
  // rounding of the true difference. It differs from the generator's own pinned
  // spread in the last tenth (1QB 12-team reads 19.9 here against 19.82 there),
  // and this side is the one that must be self-consistent: these are the exact
  // numbers the suppression note prints, so a reader who subtracts the two
  // percentages in the sentence gets the number the guard actually judged.
  // Irrelevant to the verdict either way — the threshold carries 2.5x headroom.
  const min = known ? pct1(lo) : null;
  const max = known ? pct1(hi) : null;
  return {
    heat: pct1(entry.heat),
    rooms: entry.rooms,
    minRooms: floor,
    min,
    max,
    spread: known ? Math.round((max - min) * 10) / 10 : Infinity,
  };
}

/**
 * The size cell for this format and team count, or null when nothing about it
 * can be said. THE ONE GATE, and every caller goes through it.
 *
 * Null covers every reason a size cannot be quoted at all: an unknown format,
 * an artifact built before meta.sizeHeat existed, a room outside the corpus's
 * own 10-14 team window, a cell under SIZE_HEAT_MIN_ROOMS, and a cell whose own
 * rooms disagree past SIZE_HEAT_MAX_SPREAD.
 *
 * A non-null cell carries `confident`, which is the difference between a
 * headline with a percentage in it and a headline with a direction in it.
 *
 * @param {string} benchmarkFormat "superflex" | "1qb"
 * @param {number} teams           this room's team count
 * @returns {{heat:number, rooms:number, minRooms:number, spread:number,
 *            confident:boolean}|null} heat and spread in PERCENT / points
 */
export function sizeHeatFor(benchmarkFormat, teams) {
  const cell = rawCell(benchmarkFormat, teams);
  if (!cell) return null;
  if (cell.rooms < SIZE_HEAT_MIN_ROOMS) return null;
  if (!(cell.spread <= SIZE_HEAT_MAX_SPREAD)) return null;
  return { ...cell, confident: cell.rooms >= cell.minRooms };
}

/** "about 3.7% over" / "right at it" — the reference, in the note. */
function phrase(pct) {
  const word = heatWord(pct);
  return word === "right at" ? "right at it" : `about ${Math.abs(pct).toFixed(1)}% ${word}`;
}

/** "yours paid 9.2% over" / "yours paid right at it" — the room, in the note. */
function yoursPhrase(roomHeat) {
  return heatWord(roomHeat) === "right at"
    ? "yours paid right at it"
    : `yours paid ${Math.abs(roomHeat).toFixed(1)}% ${heatWord(roomHeat)}`;
}

/**
 * The room's market claim: a headline and the line under it.
 *
 * 🔴 THE PRIMARY NUMBER IS THE ROOM AGAINST ITS OWN SIZE. `roomHeat` and the
 * size cell are the same estimator against the same player table — the per-room
 * heat api/_lib/rateReport.js computes, with the corpus taking its median
 * across rooms of a size — so the difference between them is the only part of
 * the room's number that is about how the room drafted rather than about how
 * many seats it has. The note keeps both pooled figures visible so a reader can
 * do the subtraction themselves; the headline does not make them.
 *
 * 🔴 ON A THIN CELL THE HEADLINE LOSES ITS DIGITS, NOT ITS MEANING. A direction
 * ("above what real 10-team rooms pay") is a far weaker claim than a percentage
 * and is exactly as strong as 8 tightly-agreeing rooms support; the note
 * immediately under it carries the measurement, the room count and the hedge,
 * so the number never appears without its n. What must never happen is a bare
 * thin percentage in the loudest sentence on the page.
 *
 * @param {string} opts.benchmarkFormat the cell key the room was graded in
 * @param {number} opts.teams           this room's team count
 * @param {number} opts.roomHeat        this room's own heat, in percent
 * @returns {{headline: string|null, note: string|null}} headline null means the
 *   page must make NO room-level percentage claim; the caller supplies its own
 *   room-relative words instead.
 */
export function roomMarketClaim({ benchmarkFormat, teams, roomHeat }) {
  // No market number at all — an unpriced room. Nothing to claim and nothing to
  // explain: the page's room-relative headline already says what it is doing.
  if (!Number.isFinite(roomHeat)) return { headline: null, note: null };

  const cell = sizeHeatFor(benchmarkFormat, teams);
  if (!cell) return { headline: null, note: suppressedNote(benchmarkFormat, teams) };

  const delta = Math.round((roomHeat - cell.heat) * 10) / 10;
  const word = heatWord(delta);

  if (cell.confident) {
    const headline =
      word === "right at"
        ? `your room paid right at what real ${teams}-team rooms pay`
        : `your room paid about ${Math.abs(delta).toFixed(1)}% ${word} what real ${teams}-team rooms pay`;
    return {
      headline,
      note: `Real ${teams}-team rooms typically pay ${phrase(
        cell.heat
      )} the pooled 10-14 team market; ${yoursPhrase(roomHeat)}.`,
    };
  }

  // Thin but real. Direction words rather than "over"/"under" because these
  // read as comparisons without a number in front of them, which is the whole
  // point of this branch.
  const dir = word === "right at" ? "right around" : word === "over" ? "above" : "below";
  return {
    headline: `your room paid ${dir} what real ${teams}-team rooms pay`,
    // The n is in the sentence, in the words this report already uses for a
    // thin cell elsewhere. Never a bare percentage on this branch.
    note:
      `Real ${teams}-team rooms typically pay ${phrase(cell.heat)} the pooled 10-14 team ` +
      `market, from ${cell.rooms} real rooms so far: read as direction. ` +
      `${yoursPhrase(roomHeat).replace(/^yours/, "Yours")}.`,
  };
}

/**
 * Why no percentage is being made, when none is.
 *
 * 🔴 IT NAMES WHAT IS MISSING AND WHAT IS NOT. The per-player columns further
 * down the page are still pooled and still labelled that way, so the absence of
 * a headline percent must not read as "there is no market here" — it reads as
 * "there is no market for a room THIS SIZE yet".
 */
function suppressedNote(benchmarkFormat, teams) {
  const raw = rawCell(benchmarkFormat, teams);
  const tail =
    `so there is no size-matched market to put a room-wide percentage against — room ` +
    `size alone moves that number by more than 30 points. Player prices below are still ` +
    `the pooled 10-14 team market.`;
  if (!raw) {
    return `The real-market cells cover 10-, 12- and 14-team rooms and yours is not one of them, ${tail}`;
  }
  if (raw.rooms < SIZE_HEAT_MIN_ROOMS) {
    const rooms = `${raw.rooms} real ${teams}-team ${raw.rooms === 1 ? "room" : "rooms"}`;
    return `Only ${rooms} in the corpus so far, against the ${SIZE_HEAT_MIN_ROOMS} this benchmark needs, ${tail}`;
  }
  // Enough rooms, but they do not agree — so the median is not a typical, it is
  // an average of a cluster and something broken. Quoting the extremes is the
  // honest way to say that, and it is checkable.
  return (
    `The ${raw.rooms} real ${teams}-team rooms in the corpus disagree far too widely to ` +
    `take a typical from — they run from ${raw.min.toFixed(1)}% to ${raw.max.toFixed(1)}% — ${tail}`
  );
}
